/**
 * Parsed, Workbench-shaped view of a `RolePermissionsResponse` (the flat
 * `roleName`/`userType`/`permissions[]` payload returned by
 * `ProgramRoleCapabilitiesApi.getRolePermissions`). This is the "role only"
 * layer described in `step6-permission-matrix.md` §2a/§4 — it answers
 * "can this role reach/write a Step6 section at all", not "is this field
 * read-only right now" (that combines role + deal status/phase + VLA lock,
 * and is served separately by `SectionPermissionsApi.getSectionPermissions`).
 */

/**
 * The six Step6 sections gated by a `programRoleSet` entry (matrix §1/§2a) —
 * single source of truth other permission files derive their per-section
 * types/maps/switches from, so adding a section is a one-line change here
 * instead of a hunt across `TAB_MARKERS`, `getDealStateVerdictForSection`, etc.
 */
export const SECTION_IDS = ["dealStatus", "financial", "vehicle", "driver", "secondSigner", "internal"] as const;

export type WorkbenchSectionId = (typeof SECTION_IDS)[number];

/**
 * Per-section verdict derived purely from the flat permission list.
 * `writable` already accounts for `forcedReadOnly` — never read
 * `writable` without also checking `visible`.
 */
export interface SectionCapability {
  /** The role's base marker is present (e.g. `"VEHICLE"`) — section may render at all. */
  visible: boolean;
  /** The role's `_W` marker is present AND the section is not forced read-only. */
  writable: boolean;
  /** The role's `_R` marker is present (e.g. `"VEHICLE_R"`) — section is forced read-only regardless of `_W`. */
  forcedReadOnly: boolean;
}

/** Fully parsed, Workbench-usable shape of a role's Step6 capabilities. */
export interface WorkbenchRoleCapabilities {
  /** Verbatim `roleName` from the BFF response (e.g. `"DSC FUNDING ANALYST"`). */
  roleName: string;
  /** Verbatim `userType` from the BFF response (e.g. `"CAC"` or `"DEALER"`). */
  userType: string;
  /** `userType === "CAC"` (case-insensitive) — mirrors legacy `isCacUser()`. */
  isCacUser: boolean;
  /** Per-section visible/writable/forcedReadOnly verdicts, one entry per Step6 section. */
  sections: Record<WorkbenchSectionId, SectionCapability>;
  /** The role's `EXIT` marker is present — matrix §2a/§5.6. */
  canExit: boolean;
}
